import { authService } from '@features/auth/services/authService';
import type { PickedLocation } from '../utils/guestRideUtils';

// The hoprides estimate endpoint needs an internal passphrase the browser can't
// hold, so it's proxied through our `rideshare-estimate` Netlify function. The
// function gates the trip distance against the rooftop's `max_trip_miles`.
// Prices are intentionally never returned — guest-ride surfaces don't show fares.

export type RideEstimateReason = 'ok' | 'too_far' | 'not_serviceable' | 'estimate_failed';

export interface RideEstimate {
  /** Whether the trip is within range / serviceable and may be booked. */
  allowed: boolean;
  reason: RideEstimateReason;
  serviceable: boolean;
  distanceMiles: number | null;
  maxTripMiles: number;
  durationSec: number | null;
  /** User-facing reason when `allowed` is false; null when allowed. */
  message: string | null;
}

export async function getRideEstimate(
  pickup: PickedLocation,
  dropoff: PickedLocation,
  guestridesConfig: Record<string, unknown> | null | undefined,
): Promise<RideEstimate> {
  const token = await authService.getAccessTokenWithAutoRefresh();
  const res = await fetch('/.netlify/functions/rideshare-estimate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ pickup, dropoff, guestridesConfig: guestridesConfig ?? null }),
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    const message = errData.message || 'We couldn’t check this trip just now. Please try again.';
    console.error('[estimateService.getRideEstimate] Request failed:', { status: res.status, errData });
    throw new Error(message);
  }

  return res.json();
}
